// Resolve and (optionally) shuffle the file list before lazy-loading paginates it.
//
// Logic:
//   - If FilterManager handed us exactly SEARCH_LIMIT files on an empty query / no
//     filters, replace them with the full set from filesStore so shuffle covers
//     the whole catalog (not only the first 300).
//   - Apply random shuffle only when query is empty AND no filters are active.
//   - Force a NEW seed when a search was just cleared; reuse the existing seed
//     on initial page load.
//   - Always clamp the final list to SEARCH_LIMIT.

import { SEARCH_LIMIT } from './constants.js';
import { getSearchQuery, hasActiveFilters, detectSearchTransition } from './filter-detection.js';

export function resolveFileList(files, debugMode) {
    let working = Array.isArray(files) ? files : [];

    const searchQuery = getSearchQuery();
    const isEmptyQuery = !searchQuery;
    const filtersActive = hasActiveFilters();
    const wasSearchingBefore = detectSearchTransition(searchQuery);

    // Files that come from FilterManager are already capped at SEARCH_LIMIT.
    const cameFromFilterManager =
        isEmptyQuery && working.length === SEARCH_LIMIT && files && files.length === SEARCH_LIMIT;

    // Replace FilterManager's 300-cap with the full store so shuffle is global.
    let loadedAllFiles = false;
    if (isEmptyQuery && !filtersActive && cameFromFilterManager && window.filesStore) {
        try {
            const allFiles = window.filesStore.getFiles();
            if (Array.isArray(allFiles) && allFiles.length > 0) {
                working = allFiles;
                loadedAllFiles = true;
            }
        } catch (_) {
            // Silent: store may not be ready.
        }
    }

    // Shuffle on:
    //   - initial empty load (files came directly, not from FilterManager), OR
    //   - transition from search → empty, OR
    //   - we just loaded the full store after a clear.
    const shouldShuffle = isEmptyQuery && !filtersActive && working.length > 0 && (
        !cameFromFilterManager || wasSearchingBefore === true || loadedAllFiles
    );

    if (shouldShuffle &&
        window.searchEngine &&
        typeof window.searchEngine.getRandomSeed === 'function' &&
        typeof window.searchEngine.selectRandomFiles === 'function') {
        try {
            const forceNewSeed = wasSearchingBefore === true || (cameFromFilterManager && isEmptyQuery);
            const seed = forceNewSeed
                ? window.searchEngine.getRandomSeed(true)
                : window.searchEngine.getRandomSeed(false);
            working = window.searchEngine.selectRandomFiles(working, seed);
        } catch (e) {
            if (debugMode === '1') {
                console.error('[LazyLoadManager] Random selection failed:', e);
            }
        }
    }

    const limited = working.length > SEARCH_LIMIT ? working.slice(0, SEARCH_LIMIT) : working;

    if (files && files.length > SEARCH_LIMIT && debugMode === '1') {
        console.log('[LazyLoadManager] ⚠️ Files limited at lazy-load level', {
            original: files.length,
            limited: SEARCH_LIMIT
        });
    }

    return limited;
}
